/**
 * MarkdownEditor Component
 *
 * Basic markdown editor with toolbar and preview
 * Supports: headings, lists, bold, italic, code, links
 */

import React, { useMemo } from 'react';
import SimpleMDE from 'react-simplemde-editor';
import 'easymde/dist/easymde.min.css';

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  maxLength?: number;
}

export const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  value,
  onChange,
  placeholder = 'Write your instructions in markdown...',
  disabled = false,
  maxLength = 50000,
}) => {
  const characterCount = value.length;
  const isNearLimit = characterCount > maxLength * 0.9;
  const isOverLimit = characterCount > maxLength;

  // SimpleMDE options
  const editorOptions = useMemo(() => {
    return {
      placeholder,
      spellChecker: false,
      status: false, // We'll show our own character count
      toolbar: [
        'bold',
        'italic',
        '|',
        'heading-1',
        'heading-2',
        'heading-3',
        '|',
        'unordered-list',
        'ordered-list',
        '|',
        'code',
        'quote',
        '|',
        'link',
        '|',
        'preview',
        'side-by-side',
        'fullscreen',
        '|',
        'guide',
      ] as any,
      maxHeight: '500px',
      minHeight: '300px',
    };
  }, [placeholder]);

  return (
    <div className="markdown-editor">
      <SimpleMDE
        value={value}
        onChange={onChange}
        options={editorOptions}
      />
      <div className="mt-2 flex justify-between items-center text-sm">
        <p
          className={`${
            isOverLimit
              ? 'text-red-600 font-semibold'
              : isNearLimit
              ? 'text-amber-600'
              : 'text-gray-500'
          }`}
          role="status"
          aria-live="polite"
        >
          {characterCount.toLocaleString()} / {maxLength.toLocaleString()} characters
        </p>
      </div>
      {isOverLimit && (
        <p className="mt-1 text-sm text-red-600" role="alert">
          Instruction exceeds maximum length. Please reduce by{' '}
          {(characterCount - maxLength).toLocaleString()} characters.
        </p>
      )}

      <style>{`
        .markdown-editor .EasyMDEContainer {
          border-radius: 0.375rem;
        }
        .markdown-editor .EasyMDEContainer .CodeMirror {
          border-radius: 0.375rem;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
        }
        .markdown-editor .editor-preview {
          padding: 1rem;
        }
        .markdown-editor .editor-preview h1,
        .markdown-editor .editor-preview h2,
        .markdown-editor .editor-preview h3,
        .markdown-editor .editor-preview h4,
        .markdown-editor .editor-preview h5,
        .markdown-editor .editor-preview h6 {
          margin-top: 1.5rem;
          margin-bottom: 0.5rem;
          font-weight: 600;
        }
        .markdown-editor .editor-preview h1 {
          font-size: 2rem;
        }
        .markdown-editor .editor-preview h2 {
          font-size: 1.5rem;
        }
        .markdown-editor .editor-preview h3 {
          font-size: 1.25rem;
        }
        .markdown-editor .editor-preview ul,
        .markdown-editor .editor-preview ol {
          margin-left: 1.5rem;
          margin-top: 0.5rem;
          margin-bottom: 0.5rem;
        }
        .markdown-editor .editor-preview code {
          background-color: #f3f4f6;
          padding: 0.125rem 0.25rem;
          border-radius: 0.25rem;
          font-size: 0.875rem;
        }
        .markdown-editor .editor-preview pre {
          background-color: #f3f4f6;
          padding: 1rem;
          border-radius: 0.375rem;
          overflow-x: auto;
          margin-top: 0.5rem;
          margin-bottom: 0.5rem;
        }
        .markdown-editor .editor-preview pre code {
          background-color: transparent;
          padding: 0;
        }
        .markdown-editor .editor-preview a {
          color: #2563eb;
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};
